import React, {
  /**
   * AnimatePresence
   *
   * A custom React component for managing enter/exit animations of child elements.
   *
   * - Inspired by Framer Motion's AnimatePresence, but tailored for GSAP and custom animation logic.
   * - Handles mounting/unmounting of children with exit/enter transitions.
   * - Waits for ALL children using usePresence to call safeToRemove() before unmounting.
   *
   * Usage:
   * <AnimatePresence>
   *   {condition && <Component key="unique" />}
   * </AnimatePresence>
   *
   * Accessibility:
   * - This component does not render additional DOM wrappers.
   * - Children should manage their own focus and ARIA attributes as needed.
   */
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { PresenceContext } from "./PresenceContext"

type Props = {
  children: React.ReactNode
  mode?: "sync" | "wait"
}

type ChildEntry = {
  child: React.ReactNode
  isPresent: boolean
}

export function AnimatePresence({ children, mode = "sync" }: Props) {
  const presentChildren = useMemo(
    () => React.Children.toArray(children),
    [children],
  )
  const presentKeys = useMemo(
    () =>
      new Set(presentChildren.map((c) => (isValidElement(c) ? c.key : null))),
    [presentChildren],
  )

  // Seeded from the first render's children rather than an empty Map. The
  // reconcile effect below never runs on the server, so starting empty renders
  // nothing into the prerendered HTML and makes the whole subtree client-only.
  const [rendered, setRendered] = useState<Map<React.Key, ChildEntry>>(() => {
    const initial = new Map<React.Key, ChildEntry>()

    for (const child of presentChildren) {
      if (!isValidElement(child) || !child.key) continue
      initial.set(child.key, { child, isPresent: true })
    }

    return initial
  })
  const pendingCounts = useRef(new Map<React.Key, number>())
  const registerFns = useRef(new Map<React.Key, () => () => void>())

  const isWaiting =
    mode === "wait" &&
    Array.from(rendered.values()).some((entry) => !entry.isPresent)

  const removeChild = useCallback((key: React.Key) => {
    pendingCounts.current.delete(key)
    registerFns.current.delete(key)
    setRendered((prev) => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  // Reconcile children
  useEffect(() => {
    setRendered((prev) => {
      const next = new Map(prev)

      // Mark exiting children
      for (const [key, entry] of prev) {
        if (!presentKeys.has(key as string | null) && entry.isPresent) {
          next.set(key, { ...entry, isPresent: false })

          // If nothing registered, remove immediately
          if ((pendingCounts.current.get(key) ?? 0) === 0) {
            setTimeout(() => removeChild(key), 0)
          }
        }
      }

      // Add or update present children
      if (!isWaiting) {
        for (const child of presentChildren) {
          if (!isValidElement(child)) continue
          const { key } = child
          if (!key) continue

          const existing = next.get(key)
          if (!existing || existing.isPresent) {
            next.set(key, { child, isPresent: true })
          }
        }
      }

      return next
    })
  }, [children, isWaiting, presentChildren, presentKeys, removeChild])

  const getRegisterFn = useCallback((key: React.Key) => {
    let fn = registerFns.current.get(key)
    if (!fn) {
      fn = () => {
        pendingCounts.current.set(
          key,
          (pendingCounts.current.get(key) ?? 0) + 1,
        )

        return () => {
          const count = (pendingCounts.current.get(key) ?? 1) - 1
          pendingCounts.current.set(key, count)

          if (count === 0) {
            setRendered((prev) => {
              const entry = prev.get(key)
              if (entry && !entry.isPresent) {
                const next = new Map(prev)
                next.delete(key)
                pendingCounts.current.delete(key)
                registerFns.current.delete(key)
                return next
              }
              return prev
            })
          }
        }
      }
      registerFns.current.set(key, fn)
    }
    return fn
  }, [])

  return (
    <>
      {Array.from(rendered.entries()).map(([key, { child, isPresent }]) => {
        if (!isValidElement(child)) return child

        return (
          <PresenceContext.Provider
            key={key}
            value={{
              isPresent,
              register: getRegisterFn(key),
            }}
          >
            {child}
          </PresenceContext.Provider>
        )
      })}
    </>
  )
}
